import { aabb2ByPolyLine2s, aabb2Grow, aabb2Overlaps } from "geom/aabb2";
import type { BRep, Face } from "geom/brep";
import { faceNew, transformFaceLoopBasis } from "geom/brep";
import { Line2CollinearOverlap } from "geom/core";
import { line2CollinearOverlap } from "geom/line2";
import { planeCopy, planeIsCoplanar, planeNormal } from "geom/plane";
import { polygonIntersection } from "geom/polyclip/polyClip";
import { intersectBRepWithProvenance } from "geom/polyclip/setOps3";
import { polyLine2Copy, polyLine2Edge, polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec3Dot } from "geom/vec3";

const GROW_AABB2 = 0.1;

export type GraphCoplanarFacePairIntersection = {
  readonly aFaceIndex: number;
  readonly bFaceIndex: number;
  readonly faceEdgeType: "OVERLAP" | "EDGE";
  readonly faceEdgeDot: number;
  readonly overlapFaceInAPlane?: Face;
};

export interface BRepGraphIntersectionResult {
  readonly intersectionFaces: Face[];
  readonly facePairs: GraphCoplanarFacePairIntersection[];
  readonly pairToResultFaceIndices: Map<string, number[]>;
}

interface DeferredFacePairCandidate {
  readonly aFace: Face;
  readonly bFace: Face;
  readonly aIndex: number;
  readonly bIndex: number;
  readonly transformedBFaceLoopsInAPlane: PolyLine2[];
}

export type CoplanarFacePairIntersection = {
  readonly faceEdgeType: "OVERLAP" | "EDGE";
  readonly faceEdgeDot: number;
  readonly overlapFaceInAPlane?: Face;
};

export function polyLinesShareEdge(loopA: PolyLine2, loopB: PolyLine2): boolean {
  const endTolSq = 1e-8;
  for (let i = 0; i < polyLine2EdgeCount(loopA); i++) {
    const eA = polyLine2Edge(loopA, i);
    for (let j = 0; j < polyLine2EdgeCount(loopB); j++) {
      const eB = polyLine2Edge(loopB, j);
      const overlap = line2CollinearOverlap(
        eA[0],
        eA[1],
        eA[2],
        eA[3],
        eB[0],
        eB[1],
        eB[2],
        eB[3],
        true,
        endTolSq,
        1e-4,
      );
      if (overlap !== Line2CollinearOverlap.NONE) {
        return true;
      }
    }
  }
  return false;
}

export function computeCoplanarFacePairIntersection(
  aFace: Face,
  bFace: Face,
  aabbGrow: number = GROW_AABB2,
  transformedBLoopsInAPlane?: PolyLine2[],
  skipBroadPhase: boolean = false,
): CoplanarFacePairIntersection | null {
  if (!planeIsCoplanar(aFace.plane, bFace.plane)) {
    return null;
  }

  const transformedBLoops =
    transformedBLoopsInAPlane ??
    bFace.loops.map((loop) => transformFaceLoopBasis(polyLine2Copy(loop), bFace.plane, aFace.plane));

  if (!skipBroadPhase) {
    const aFaceAabb = aabb2ByPolyLine2s(aFace.loops);
    const bFaceAabb = aabb2ByPolyLine2s(transformedBLoops);
    if (!aFaceAabb || !bFaceAabb) {
      return null;
    }
    aabb2Grow(aFaceAabb, aabbGrow, aabbGrow);
    aabb2Grow(bFaceAabb, aabbGrow, aabbGrow);
    if (!aabb2Overlaps(aFaceAabb, bFaceAabb)) {
      return null;
    }
  }

  const [faceANx, faceANy, faceANz] = planeNormal(aFace.plane);
  const [faceBNx, faceBNy, faceBNz] = planeNormal(bFace.plane);
  const faceEdgeDot = vec3Dot(faceANx, faceANy, faceANz, faceBNx, faceBNy, faceBNz);

  const intersectionResult = polygonIntersection({
    pls: [aFace.loops, transformedBLoops],
  });
  if (intersectionResult.length > 0) {
    const intersectionPoly = intersectionResult[0];
    if (intersectionPoly && intersectionPoly.length > 0) {
      return {
        faceEdgeType: "OVERLAP",
        faceEdgeDot,
        overlapFaceInAPlane: faceNew(intersectionPoly, planeCopy(aFace.plane)),
      };
    }
  }

  const loopA = aFace.loops[0];
  const loopB = transformedBLoops[0];
  if (loopA && loopB && polyLinesShareEdge(loopA, loopB)) {
    return {
      faceEdgeType: "EDGE",
      faceEdgeDot,
    };
  }

  return null;
}

/**
 * Coplanar face-pair adjacency only — skips 3D boolean intersection.
 * Used when same-level room outlines have no 2D overlap area.
 */
export function computeCoplanarFacePairsForGenerateBuildingGraph(a: BRep, b: BRep): BRepGraphIntersectionResult {
  const facePairs: GraphCoplanarFacePairIntersection[] = [];

  for (let aIndex = 0; aIndex < a.faces.length; aIndex++) {
    const aFace = a.faces[aIndex];
    if (!aFace) {
      continue;
    }

    const aabb2AFace = aabb2ByPolyLine2s(aFace.loops);
    if (!aabb2AFace) {
      continue;
    }
    aabb2Grow(aabb2AFace, GROW_AABB2, GROW_AABB2);

    for (let bIndex = 0; bIndex < b.faces.length; bIndex++) {
      const bFace = b.faces[bIndex];
      if (!bFace) {
        continue;
      }

      if (!planeIsCoplanar(aFace.plane, bFace.plane)) {
        continue;
      }

      const transformedBFaceLoopsInAPlane = bFace.loops.map((loop) =>
        transformFaceLoopBasis(polyLine2Copy(loop), bFace.plane, aFace.plane),
      );
      const aabb2BFace = aabb2ByPolyLine2s(transformedBFaceLoopsInAPlane);
      if (!aabb2BFace) {
        continue;
      }
      aabb2Grow(aabb2BFace, GROW_AABB2, GROW_AABB2);
      if (!aabb2Overlaps(aabb2AFace, aabb2BFace)) {
        continue;
      }

      const intersection = computeCoplanarFacePairIntersection(
        aFace,
        bFace,
        GROW_AABB2,
        transformedBFaceLoopsInAPlane,
        true,
      );
      if (!intersection) {
        continue;
      }

      facePairs.push({
        aFaceIndex: aIndex,
        bFaceIndex: bIndex,
        faceEdgeType: intersection.faceEdgeType,
        faceEdgeDot: intersection.faceEdgeDot,
        overlapFaceInAPlane: intersection.overlapFaceInAPlane,
      });
    }
  }

  return {
    intersectionFaces: [],
    facePairs,
    pairToResultFaceIndices: new Map(),
  };
}

export function intersectBRepForGenerateBuildingGraph(a: BRep, b: BRep): BRepGraphIntersectionResult {
  const deferredFacePairCandidates: DeferredFacePairCandidate[] = [];
  const intersectResult = intersectBRepWithProvenance(a, b, {
    onFacePair: ({ aFace, bFace, aIndex, bIndex, transformedBFaceLoopsInAPlane }) => {
      deferredFacePairCandidates.push({
        aFace,
        bFace,
        aIndex,
        bIndex,
        transformedBFaceLoopsInAPlane,
      });
    },
  });

  let intersectionFaces = intersectResult.faces;
  let pairToResultFaceIndices = intersectResult.pairToResultFaceIndices;
  // `intersectBRep` can return a single face for coplanar overlap between solids (a sheet, not a volume).
  // That case should use coplanar `facePairs` + shared-wall face edges, not provenance-driven brep overlap.
  // So - we suppress the intersection faces in this case and defer to the face pairs.
  if (intersectionFaces.length === 1) {
    intersectionFaces = [];
    pairToResultFaceIndices = new Map();
  }

  const facePairs: GraphCoplanarFacePairIntersection[] = [];
  // `facePairs` are only consumed for base no-brep (touch/coplanar-adjacent) edges.
  // If boolean intersection already produced faces, overlap handling uses provenance
  // (`pairToResultFaceIndices`) + synthetic edges instead, so this work is unnecessary.
  // When a one-face "intersection" is suppressed (coplanar overlap), use `facePairs` instead.
  if (intersectionFaces.length === 0) {
    for (const candidate of deferredFacePairCandidates) {
      const intersection = computeCoplanarFacePairIntersection(
        candidate.aFace,
        candidate.bFace,
        GROW_AABB2,
        candidate.transformedBFaceLoopsInAPlane,
        true,
      );
      if (!intersection) {
        continue;
      }

      facePairs.push({
        aFaceIndex: candidate.aIndex,
        bFaceIndex: candidate.bIndex,
        faceEdgeType: intersection.faceEdgeType,
        faceEdgeDot: intersection.faceEdgeDot,
        overlapFaceInAPlane: intersection.overlapFaceInAPlane,
      });
    }
  }

  return {
    intersectionFaces,
    facePairs,
    pairToResultFaceIndices,
  };
}
